import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../common/types';
import { useUserTokenDecoded } from '../../redux/slices/appInfo/hooks';
import { withBrandWord } from '../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole handoff; each part uses its own slice of it */
import styles from './MobileApps.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HandoffStepsProps {
  serviceName: string;
  /** Design 2a: all three steps are done. */
  isDone?: boolean;
  /** Design your-intelligence k2: the login happens on the page of the service (Ditto Music). */
  logsInOnServicePage?: boolean;
}

const bold = (chunks: React.ReactNode[]) => <b>{withBrandWord(chunks)}</b>;

/**
 * Design 1a: the three steps on the phone. Step 2 names the signed-in email, because a different
 * account in the app is the most likely failure.
 */
const HandoffStepsComponent: React.FC<HandoffStepsProps> = ({
  serviceName,
  isDone = false,
  logsInOnServicePage = false,
}) => {
  const intl = useIntl();
  const email = useUserTokenDecoded()?.email ?? '';

  const steps = [
    intl.formatMessage(
      { id: 'app.sml.mobileApps.steps.scan', defaultMessage: '<b>Scan the code</b> with your phone camera.' },
      { b: bold }
    ),
    intl.formatMessage(
      {
        id: 'app.sml.mobileApps.steps.install',
        defaultMessage: '<b>Install MUSIC24</b>, or open it if you have it. Sign in as {email}.',
      },
      { b: bold, email }
    ),
    logsInOnServicePage
      ? intl.formatMessage(
          {
            id: 'app.sml.mobileApps.steps.connectOnServicePage',
            defaultMessage:
              "The app opens on <b>Catalog</b>. Tap <b>Connect</b> on {service} and log in on {service}'s page.",
          },
          { b: bold, service: serviceName }
        )
      : intl.formatMessage(
          {
            id: 'app.sml.mobileApps.steps.connectCatalog',
            defaultMessage: 'The app opens on <b>Catalog</b>. Tap <b>Connect</b> on {service}.',
          },
          { b: bold, service: serviceName }
        ),
  ];

  return (
    <ol className={styles.steps}>
      {steps.map((text, index) => (
        // eslint-disable-next-line react/no-array-index-key -- a fixed list of three steps
        <li key={index} className={clsx(styles.step, isDone ? styles.stepDone : index === 0 && styles.stepNow)}>
          <span className={styles.stepNumber}>{isDone ? <FontAwesomeIcon icon={faCheck} /> : index + 1}</span>
          <span>{text}</span>
        </li>
      ))}
    </ol>
  );
};

export const HandoffSteps = TypedMemo(HandoffStepsComponent);
